// Google Analytics 4 tracking utilities

declare global {
  interface Window {
    dataLayer?: unknown[][];
    gtag?: (command: string, ...args: unknown[]) => void;
  }
}

// Initialize gtag function if not available
const gtag = (...args: Parameters<NonNullable<Window['gtag']>>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
};

// Track page views
export const trackPageView = (url: string, title?: string) => {
  gtag('config', 'G-3T551R7ECG', {
    page_path: url,
    page_title: title,
  });
};

// Track custom events
export const trackEvent = (
  eventName: string, 
  parameters: Record<string, unknown> = {}
) => {
  gtag('event', eventName, {
    event_category: parameters.category || 'engagement',
    event_label: parameters.label,
    value: parameters.value,
    ...parameters,
  });
};

// CTA Button Clicks
export const trackCTAClick = (
  buttonText: string,
  location: string,
  additionalData?: Record<string, unknown>
) => {
  trackEvent('cta_click', {
    category: 'user_engagement',
    label: `${buttonText} - ${location}`,
    button_text: buttonText,
    button_location: location,
    ...additionalData,
  });
};

// Beta Program Interactions
export const trackBetaSignupStart = (source: string) => {
  trackEvent('beta_signup_start', {
    category: 'conversion',
    label: `Beta signup started from ${source}`,
    signup_source: source,
  });
};

export const trackBetaSignupSubmit = (formData: {
  deviceType?: string;
  experience?: string;
  motivation?: string;
}) => {
  trackEvent('beta_signup_submit', {
    category: 'conversion',
    label: 'Beta signup form submitted',
    device_type: formData.deviceType,
    user_experience: formData.experience,
    user_motivation: formData.motivation,
  });
};

export const trackBetaSignupSuccess = () => {
  trackEvent('beta_signup_success', {
    category: 'conversion',
    label: 'Beta signup completed successfully',
  });
};

export const trackBetaSignupError = (error: string) => {
  trackEvent('beta_signup_error', {
    category: 'error',
    label: 'Beta signup failed',
    error_message: error,
  });
};

// Navigation & Menu Interactions
export const trackNavigation = (destination: string, source: string) => {
  trackEvent('navigation_click', {
    category: 'navigation',
    label: `Navigate to ${destination} from ${source}`,
    destination_page: destination,
    source_location: source,
  });
};

export const trackMobileMenuToggle = (action: 'open' | 'close') => {
  trackEvent('mobile_menu_toggle', {
    category: 'interface',
    label: `Mobile menu ${action}`,
    menu_action: action,
  });
};

// Theme & Settings
export const trackThemeChange = (newTheme: 'light' | 'dark', method: 'manual' | 'auto') => {
  trackEvent('theme_change', {
    category: 'interface',
    label: `Theme changed to ${newTheme}`,
    new_theme: newTheme,
    change_method: method,
  });
};

export const trackLanguageChange = (newLanguage: string, oldLanguage: string) => {
  trackEvent('language_change', {
    category: 'interface',
    label: `Language changed to ${newLanguage}`,
    new_language: newLanguage,
    old_language: oldLanguage,
  });
};

// Content Engagement
export const trackFeatureInterest = (featureName: string, interactionType: string) => {
  trackEvent('feature_interest', {
    category: 'engagement',
    label: `Interested in ${featureName}`,
    feature_name: featureName,
    interaction_type: interactionType,
  });
};

export const trackTestimonialView = (testimonialAuthor: string) => {
  trackEvent('testimonial_view', {
    category: 'engagement',
    label: `Viewed testimonial by ${testimonialAuthor}`,
    author: testimonialAuthor,
  });
};

// Pricing & Plan Interest
export const trackPricingView = (planName?: string) => {
  trackEvent('pricing_view', {
    category: 'business',
    label: planName ? `Viewed ${planName} plan` : 'Viewed pricing page',
    plan_name: planName,
  });
};

export const trackPlanInterest = (planName: string, planPrice: string) => {
  trackEvent('plan_interest', {
    category: 'business',
    label: `Interested in ${planName}`,
    plan_name: planName,
    plan_price: planPrice,
    currency: 'USD',
  });
};

// Support & Help
export const trackSupportInteraction = (interactionType: string, topic?: string) => {
  trackEvent('support_interaction', {
    category: 'support',
    label: `${interactionType}${topic ? ` - ${topic}` : ''}`,
    interaction_type: interactionType,
    support_topic: topic,
  });
};

// Social Sharing & External Links
export const trackSocialShare = (platform: string, content: string) => {
  trackEvent('social_share', {
    category: 'social',
    label: `Shared on ${platform}`,
    social_platform: platform,
    shared_content: content,
  });
};

export const trackExternalLinkClick = (url: string, linkText: string) => {
  trackEvent('external_link_click', {
    category: 'outbound',
    label: `Clicked external link: ${linkText}`,
    external_url: url,
    link_text: linkText,
  });
};

// Performance & User Experience
export const trackFormError = (formName: string, fieldName: string, errorType: string) => {
  trackEvent('form_error', {
    category: 'error',
    label: `${formName} form error in ${fieldName}`,
    form_name: formName,
    field_name: fieldName,
    error_type: errorType,
  });
};

export const trackPageLoadTime = (loadTime: number) => {
  trackEvent('page_load_time', {
    category: 'performance',
    label: 'Page loaded',
    value: Math.round(loadTime),
    load_time_ms: loadTime,
  });
};
